import { QUESTION_TYPES } from '@api/instant_survey';
import type { Question } from '@api/instant_survey/types';

import { HIERARCHY_PROMPT_CONFIG, SCREENER_PROMPT_TOKEN_REGEX } from './constants';

export type HierarchyPromptKey = keyof typeof HIERARCHY_PROMPT_CONFIG;
type PromptValueMap = Record<string, string>;

const hierarchyPromptKeys: HierarchyPromptKey[] = [
  'product_hierarchy',
  'category_hierarchy',
  'store_hierarchy',
];

const addPromptKeysFromText = (text: string | undefined, keys: Set<string>) => {
  if (!text) return;

  SCREENER_PROMPT_TOKEN_REGEX.lastIndex = 0;
  let match;

  while ((match = SCREENER_PROMPT_TOKEN_REGEX.exec(text)) !== null) {
    keys.add(match[1]);
  }
};

export const getQuestionTypeLabel = (type: string) => {
  if (type === QUESTION_TYPES.RADIO.value) return QUESTION_TYPES.RADIO.name;
  return type;
};

/**
 * Collects editable screener prompt keys from the question and answers.
 * Only plain mustache tokens like {{place}} are treated as editable prompt keys.
 */
export const getScreenerPromptKeys = (question?: null | Question): string[] => {
  const keys = new Set<string>();

  addPromptKeysFromText(question?.text, keys);
  question?.answers?.forEach((answer) => addPromptKeysFromText(answer.text, keys));

  return Array.from(keys);
};

const getPromptKeysFromText = (text: string | undefined) => {
  const keys = new Set<string>();
  addPromptKeysFromText(text, keys);
  return keys;
};

export const getHierarchyPromptKeys = (question?: null | Question): HierarchyPromptKey[] => {
  const keys = getPromptKeysFromText(question?.text);

  if (!hierarchyPromptKeys.every((promptKey) => keys.has(promptKey))) {
    question?.answers?.forEach((answer) => addPromptKeysFromText(answer.text, keys));
  }

  return hierarchyPromptKeys.filter((promptKey) => keys.has(promptKey));
};

export const getHierarchyPromptOptions = (
  question: null | Question,
  promptKey: HierarchyPromptKey
) => {
  const { property } = HIERARCHY_PROMPT_CONFIG[promptKey];

  return (question?.properties[property] || []).map((value) => ({
    label: humanizeScreenerPromptKey(value),
    value,
  }));
};

/**
 * Builds the editable prompt state for the preview modal from any saved backend values.
 */
export const getInitialScreenerPromptValues = (question: null | Question): PromptValueMap => {
  const promptValues: PromptValueMap = { ...(question?.screenerValueMap || {}) };

  getHierarchyPromptKeys(question).forEach((promptKey) => {
    const savedValue = promptValues[promptKey]?.trim();
    const options = getHierarchyPromptOptions(question, promptKey);

    promptValues[promptKey] = savedValue || (options[0]?.value ? `[${options[0].value}]` : '');
  });

  return promptValues;
};

/**
 * Creates the screener_value_map payload by keeping only filled prompt values.
 * Question and option text stay tokenized; this map carries the user's replacements.
 */
export const buildScreenerValueMap = (
  promptKeys: string[],
  promptValues: PromptValueMap
): PromptValueMap =>
  promptKeys.reduce<PromptValueMap>((valueMap, promptKey) => {
    const value = promptValues[promptKey]?.trim();

    if (value) {
      valueMap[promptKey] = value;
    }

    return valueMap;
  }, {});

type ScreenerPromptKind = 'date-range' | 'free-text' | 'hierarchy';

const getScreenerPromptKind = (question: Question, promptKey: string): ScreenerPromptKind => {
  if (promptKey in HIERARCHY_PROMPT_CONFIG) return 'hierarchy';

  return question.properties.screenerPromptTypes?.[promptKey] === 'date_range'
    ? 'date-range'
    : 'free-text';
};

/**
 * Groups submit-time guidance by prompt behavior. Prompt types only affect guidance;
 * all non-whitespace values are valid prompt values.
 */
export const getMissingScreenerPromptMessages = (
  question: Question,
  missingPromptKeys: string[]
) => {
  const promptKeysByKind = missingPromptKeys.reduce<Record<ScreenerPromptKind, string[]>>(
    (keys, promptKey) => {
      keys[getScreenerPromptKind(question, promptKey)].push(promptKey);
      return keys;
    },
    { 'date-range': [], 'free-text': [], hierarchy: [] }
  );
  const labels = (promptKeys: string[]) => promptKeys.map(getScreenerPromptLabel).join(', ');
  const messages: string[] = [];

  if (promptKeysByKind['free-text'].length > 0) {
    messages.push(`Enter a value for: ${labels(promptKeysByKind['free-text'])}.`);
  }
  if (promptKeysByKind['date-range'].length > 0) {
    messages.push(
      `You must specify a date range for: ${labels(promptKeysByKind['date-range'])}. Use a format similar to 12 months, 4 weeks, 1 year, or 70 days.`
    );
  }
  if (promptKeysByKind.hierarchy.length > 0) {
    messages.push(`Enter or choose a value for: ${labels(promptKeysByKind.hierarchy)}.`);
  }

  return messages;
};

/**
 * Hydrates tokenized screener text for display from a saved value map.
 * Missing or blank map entries leave their original {{token}} text untouched.
 */
export const replaceScreenerPromptTokensWithValueMap = (
  text: string | undefined,
  screenerValueMap: PromptValueMap = {}
) => {
  if (!text) return '';

  SCREENER_PROMPT_TOKEN_REGEX.lastIndex = 0;

  return text.replace(SCREENER_PROMPT_TOKEN_REGEX, (token, promptKey) => {
    const value = screenerValueMap[promptKey]?.trim();

    return value || token;
  });
};

/**
 * Converts prompt keys into readable labels for validation messages.
 * For example, "purchase_window" becomes "Purchase window".
 */
export const humanizeScreenerPromptKey = (promptKey: string) =>
  promptKey
    .replace(/(.+)brand$/i, '$1 brand')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\w/, (character) => character.toUpperCase());

export const getScreenerPromptLabel = (promptKey: string) =>
  promptKey in HIERARCHY_PROMPT_CONFIG
    ? HIERARCHY_PROMPT_CONFIG[promptKey as HierarchyPromptKey].label
    : humanizeScreenerPromptKey(promptKey);

export const toScreenerPromptTestId = (promptKey: string) =>
  `screener-prompt-${promptKey.replace(/_/g, '-')}`;
